/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

/* PACKAGES */
import { Helmet } from "react-helmet";
import DataTable from "react-data-table-component";
import { toast } from "react-hot-toast";
import moment from "moment";

/* COMPONENTS */
import Input from "components/input";
import { Button, Dialog, DialogBody } from "@material-tailwind/react";
import ExcelExport from "components/export/Export";

/* API */
import {
  deleteStartupCellApplication,
  getStartupCellApplications,
} from "service/api";

/* ICONS */
import { FaTrash } from "react-icons/fa6";
import { IoClose, IoEye } from "react-icons/io5";
import { IMAGE_URL } from "config";

/* LOADING SCREEN FOR DATATABLE */
const LoadingScreen = () => {
  return (
    <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
      <p className="text-sm font-ibm-semibold text-black text-center">
        Please wait!
      </p>
      <p className="text-sm font-ibm-semibold text-black text-center">
        We are fetching data from the database...
      </p>
    </div>
  );
};

/* NO DATA SCREEN */
const NoDataScreen = () => {
  return (
    <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
      <p className="text-sm font-ibm-semibold text-black text-center">
        There are no queries to display.
      </p>
    </div>
  );
};

const StartupApplications = () => {
  /* LOADING STATE */
  const [loading, setLoading] = useState(false);

  /* PAGINATION */
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);

  /* TOTAL DOCUMENTS */
  const [totalDocs, setTotalDocs] = useState(0);

  /* QUERIES */
  const [contactQueries, setContactQueries] = useState([]);

  /* VIEW DIALOG STATES */
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);

  /* SEARCHBAR */
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  /* DELETE MODAL */
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  /* HANDLE PAGE CHANGE */
  const handlePageChange = async (newPage) => {
    setPage(newPage);
  };

  /* HANDLE PER PAGE CHANGE */
  const handlePerPageChange = async (newSize, newPage) => {
    setSizePerPage(newSize);
    setPage(newPage);
  };

  /* GET DATA FUNCTION */
  const getDetailsFn = async (search) => {
    setLoading(true);

    const payload = {
      page: page,
      sizePerPage: sizePerPage,
      pagination: true,
    };

    if (search) {
      payload.search = search;
    }

    try {
      const response = await getStartupCellApplications(payload);
      console.log("%c Line:585 🥑 response", "color:#fca650", response);
      setContactQueries(response?.data?.docs);
      setTotalDocs(response?.data?.totalDocs);
    } catch (e) {
      console.log("Error: ", e);
    } finally {
      setLoading(false);
    }
  };

  /* HANDLE DELETE FN */
  const handleDelete = () => {
    setDeleteLoading(true);

    deleteStartupCellApplication(deleteId)
      .then(() => {
        toast.success("The query has been deleted successfully.");
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message);
      })
      .finally(() => {
        setDeleteModal(!deleteModal);
        setDeleteId(null);
        setDeleteLoading(false);
        getDetailsFn();
      });
  };

  /* COLUMNS */
  const columns = [
    {
      name: "Sr. No",
      grow: 0.5,
      selector: (row, index) => {
        return (
          <p className="text-sm font-source-regular text-greyText">
            {(page - 1) * sizePerPage + index + 1}
          </p>
        );
      },
    },
    {
      name: "Name",
      selector: (row) => {
        return (
          <p className="text-sm font-source-semibold text-black">
            {row?.firstName} {row?.lastName}
          </p>
        );
      },
    },
    {
      name: "Email address",
      selector: (row) => {
        return (
          <>
            <a
              href={`mailto:${row?.email}`}
              className="block text-sm font-source-regular text-blue-500"
            >
              {row?.email}
            </a>
            <a
              href={`mailto:${row?.phoneNumber}`}
              className="block mt-1 text-sm font-source-regular text-blue-500"
            >
              {row?.phoneNumber}
            </a>
          </>
        );
      },
    },
    {
      name: "Startup Name",
      selector: (row) => {
        return (
          <p
            style={{ overflowWrap: "anywhere" }}
            className="text-sm font-source-medium text-black"
          >
            {row?.startupName}
          </p>
        );
      },
    },
    {
      name: "Submitted on",
      selector: (row) => {
        return (
          <>
            <p
              style={{ overflowWrap: "anywhere" }}
              className="text-sm font-source-regular text-greyText"
            >
              {moment(row?.createdAt).format("MMM DD, YYYY")}
            </p>
            <p
              style={{ overflowWrap: "anywhere" }}
              className="text-sm font-source-regular text-greyText"
            >
              {moment(row?.createdAt).format("hh:mm A")}
            </p>
          </>
        );
      },
    },
    {
      name: "Actions",
      selector: (row) => {
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setViewDialog(!viewDialog);
                setSelectedQuery(row);
              }}
              className="p-2.5 bg-white border border-authBorder rounded-full hover:bg-logoYellow/10 hover:border-logoYellow transition-all duration-300 ease-in-out"
            >
              <IoEye className="w-3.5 h-3.5 text-greyText" />
            </button>
            <button
              onClick={() => {
                setDeleteModal(!deleteModal);
                setDeleteId(row?._id);
              }}
              className="p-2.5 bg-white border border-authBorder rounded-full hover:bg-logoYellow/10 hover:border-logoYellow transition-all duration-300 ease-in-out"
            >
              <FaTrash className="w-3.5 h-3.5 text-greyText" />
            </button>
          </div>
        );
      },
    },
  ];

  /* USE EFFECT */
  useEffect(() => {
    getDetailsFn(debouncedSearch);
  }, [debouncedSearch, page, sizePerPage]);

  /* USE EFFECT */
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const exportData = contactQueries.map((row, index) => ({
    "Sr. No": (page - 1) * sizePerPage + index + 1,
    Name: `${row?.firstName || ""} ${row?.lastName || ""}`.trim(),
    "Email Address": row?.email,
    "Phone Number": row?.phoneNumber,
    "Startup Name": row?.startupName,
    "Brand Name": row?.brandName,
    "Founding Year": row?.foundingYear,
    Website: row?.website,
    "Social Media Handles": row?.socialMediaHandles
      ? Object.entries(row?.socialMediaHandles)
          .map(
            ([platform, url]) =>
              `${platform.charAt(0).toUpperCase() + platform.slice(1)}: ${url}`
          )
          .join(", ")
      : "",
    "Total Full-Time Founders": row?.totalFullTimeFounders,
    "Founders' LinkedIn Profiles": row?.foundersLinkedInProfiles
      ? Object.values(row?.foundersLinkedInProfiles).join(", ")
      : "",
    Sector: row?.sector,
    "Funding Stage": row?.fundingStage,
    "Funding Raised (INR)": row?.fundingRaised,
    "Last 12 Months Revenue (INR)": row?.last12MonthsRevenue,
    "Startup Description": row?.startupDescription,
    "Incubation Program Details": row?.incubationProgramDetails,
    "Press Coverage And Awards": row?.pressCoverageAndAwards,
    "Heard From Us Via": row?.heardFromUsVia,
    "Pitch Deck": row?.pitchDeck ? `${IMAGE_URL}${row?.pitchDeck}` : "",
    "Elevator Pitch Video": row?.elevatorPitchVideo
      ? `${IMAGE_URL}${row?.elevatorPitchVideo}`
      : "",
    "Submitted Date": row?.createdAt
      ? moment(row?.createdAt).format("MMM DD, YYYY")
      : "",
    "Submitted Time": row?.createdAt
      ? moment(row?.createdAt).format("hh:mm A")
      : "",
  }));

  return (
    <section className="p-2 xl:p-5 lg:p-5 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Application Queries | Lohana Startup Cell</title>
      </Helmet>

      <div className="container-full-width">
        {/* HEADING */}
        <div className="block xl:flex lg:flex items-center justify-between">
          <div className="p-0">
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              Application forms
            </h1>
            <h2 className="text-base font-ibm-regular text-greyText">
              Manage the applications by your visitors from your startup cell
              website.
            </h2>
          </div>

          <div className="w-full xl:w-auto lg:w-auto mt-2 xl:mt-0 lg:mt-0 flex flex-col xl:flex-row lg:flex-row gap-2 xl:items-center lg:items-center">
            {/* SEARCHBAR */}
            <div className="w-full xl:w-[30vh] lg:w-[30vh]">
              <Input
                type="text"
                placeholder="Search by name..."
                name="search"
                id="search"
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
              />
            </div>

            {exportData.length > 0 && (
              <ExcelExport
                data={exportData}
                fileName="Startup_Applications"
                btnText="Export data"
                onClickFn={() => {}}
              />
            )}
          </div>
        </div>

        {/* TABLE */}
        <div className="mt-5">
          <DataTable
            className="data-table"
            columns={columns}
            data={contactQueries}
            progressPending={loading}
            progressComponent={<LoadingScreen />}
            pagination
            paginationServer
            paginationTotalRows={totalDocs}
            onChangeRowsPerPage={handlePerPageChange}
            onChangePage={handlePageChange}
            noDataComponent={<NoDataScreen />}
          />
        </div>
      </div>

      {/* DELETE DIALOG */}
      <Dialog
        open={deleteModal}
        handler={() => setDeleteModal(!deleteModal)}
        size="xs"
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <DialogBody className="p-0">
          <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
            <h5 className="text-lg font-ibm-bold text-black tracking-tight">
              Delete information
            </h5>
            <Button
              className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
              onClick={() => setDeleteModal(!deleteModal)}
            >
              <IoClose className="w-4 h-4 text-greyText" />
            </Button>
          </div>
          <div className="p-5 border-b border-authBorder">
            <p className="text-base font-ibm-regular text-black">
              Are you sure you want to delete this application from the
              database?
            </p>
          </div>
          <div className="flex items-center justify-end px-5 py-3">
            <Button
              onClick={handleDelete}
              className="text-sm shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md font-ibm-semibold text-white transition-all duration-300 ease-in-out normal-case"
            >
              {deleteLoading ? "Please wait.." : "Delete"}
            </Button>
          </div>
        </DialogBody>
      </Dialog>

      {/* VIEW DIALOG */}
      <Dialog
        open={viewDialog}
        handler={() => setViewDialog(!viewDialog)}
        size="sm"
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <DialogBody className="p-0">
          {/* HEADING */}
          <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
            <h5 className="text-lg font-ibm-bold text-black tracking-tight">
              {selectedQuery?.startupName} details
            </h5>
            <Button
              className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
              onClick={() => {
                setViewDialog(!viewDialog);
              }}
            >
              <IoClose className="w-4 h-4 text-greyText" />
            </Button>
          </div>

          {/* BODY */}
          <div className="p-5 max-h-[60vh] overflow-auto">
            {/* FOUNDER'S DETAILS */}
            <div className="bg-white shadow border border-authBorder rounded-lg p-5">
              <div className="pb-3 border-b border-authBorder">
                <h5 className="text-lg font-ibm-bold text-black uppercase">
                  Founder's contact details
                </h5>
              </div>

              <div className="pt-3">
                <p className="text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">Name: </span>{" "}
                  {selectedQuery?.firstName} {selectedQuery?.lastName}
                </p>
                <p className="block mt-1 text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">Email: </span>{" "}
                  <a
                    href={`mailto:${selectedQuery?.email}`}
                    className="underline text-blue-500"
                  >
                    {selectedQuery?.email}
                  </a>
                </p>
                <p className="block mt-1 text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">Phone number: </span>{" "}
                  <a
                    href={`tel:${selectedQuery?.phoneNumber}`}
                    className="underline text-blue-500"
                  >
                    {selectedQuery?.phoneNumber}
                  </a>
                </p>
              </div>
            </div>

            {/* STARTUP DETAILS */}
            <div className="mt-5 bg-white shadow border border-authBorder rounded-lg p-5">
              <div className="pb-3 border-b border-authBorder">
                <h5 className="text-lg font-ibm-bold text-black uppercase">
                  Startup details
                </h5>
              </div>

              <div className="pt-3">
                <p className="text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">Startup Name: </span>{" "}
                  {selectedQuery?.startupName}
                </p>
                <p className="mt-1 text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">Brand Name: </span>{" "}
                  {selectedQuery?.brandName}
                </p>
                <p className="mt-1 text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">Founding year: </span>{" "}
                  {selectedQuery?.foundingYear}
                </p>

                <p className="mt-1 text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">Website Link: </span>{" "}
                  <a
                    href={selectedQuery?.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline"
                  >
                    {selectedQuery?.website}
                  </a>
                </p>
                <p className="block mt-1 text-base font-ibm-semibold text-black">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-ibm-regular">
                      Social Media Handles:{" "}
                    </span>{" "}
                    {selectedQuery?.socialMediaHandles &&
                      Object.entries(selectedQuery?.socialMediaHandles).map(
                        ([platform, url]) => (
                          <div key={platform}>
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 underline"
                            >
                              {platform.charAt(0).toUpperCase() +
                                platform.slice(1)}
                            </a>
                          </div>
                        )
                      )}
                  </div>
                </p>
                <p className="mt-1 text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">
                    Full-time founders in the startup:{" "}
                  </span>{" "}
                  {selectedQuery?.totalFullTimeFounders}
                </p>
                <p className="block mt-1 text-base font-ibm-semibold text-black">
                  <p className="font-ibm-regular">
                    Founders' linkedin handles:{" "}
                  </p>{" "}
                  <div className="mt-1 flex items-center gap-3 flex-wrap">
                    {selectedQuery?.foundersLinkedInProfiles &&
                      Object.entries(
                        selectedQuery?.foundersLinkedInProfiles
                      ).map(([platform, url], index) => (
                        <div key={platform}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 underline"
                          >
                            Founder {index + 1}
                          </a>
                        </div>
                      ))}
                  </div>
                </p>
              </div>
            </div>

            {/* SECTOR AND FUNDING */}
            <div className="mt-5 bg-white shadow border border-authBorder rounded-lg p-5">
              <div className="pb-3 border-b border-authBorder">
                <h5 className="text-lg font-ibm-bold text-black uppercase">
                  Sector and Funding
                </h5>
              </div>

              <div className="pt-3">
                <p className="text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">
                    Which sector best describes your work?{" "}
                  </span>{" "}
                  {selectedQuery?.sector}
                </p>
                <p className="mt-1 text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">Funding stage:</span>{" "}
                  {selectedQuery?.fundingStage}
                </p>
                <p className="mt-1 text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">
                    Total funding raised till date in INR:
                  </span>{" "}
                  {parseFloat(selectedQuery?.fundingRaised).toLocaleString(
                    "en-IN"
                  )}
                </p>
                <p className="mt-1 text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">
                    Last 12 months revenue in INR:
                  </span>{" "}
                  {parseFloat(
                    selectedQuery?.last12MonthsRevenue
                  ).toLocaleString("en-IN")}
                </p>
              </div>
            </div>

            {/* ADDITIONAL DETAILS */}
            <div className="mt-5 bg-white shadow border border-authBorder rounded-lg p-5">
              <div className="pb-3 border-b border-authBorder">
                <h5 className="text-lg font-ibm-bold text-black uppercase">
                  Additional details
                </h5>
              </div>

              <div className="pt-3">
                <div className="p-0">
                  <p className="text-base font-ibm-regular text-greyText">
                    Describe your startup in one line
                  </p>
                  <p className="text-base font-ibm-semibold text-black">
                    {selectedQuery?.startupDescription}
                  </p>
                </div>
                <div className="pt-3">
                  <p className="text-base font-ibm-regular text-greyText">
                    Were or are you part of any incubator? If yes, which one?
                  </p>
                  <p className="text-base font-ibm-semibold text-black">
                    {selectedQuery?.incubationProgramDetails}
                  </p>
                </div>
                <div className="pt-3">
                  <p className="text-base font-ibm-regular text-greyText">
                    Press Coverage / Awards
                  </p>
                  <p className="text-base font-ibm-semibold text-black">
                    {selectedQuery?.pressCoverageAndAwards}
                  </p>
                </div>
                <div className="pt-3">
                  <p className="text-base font-ibm-regular text-greyText">
                    How did you hear about us?
                  </p>
                  <p className="text-base font-ibm-semibold text-black">
                    {selectedQuery?.heardFromUsVia}
                  </p>
                </div>
              </div>
            </div>

            {/* PITCH DECK */}
            <div className="mt-5 bg-white shadow border border-authBorder rounded-lg p-5">
              <div className="pb-3 border-b border-authBorder">
                <h5 className="text-lg font-ibm-bold text-black uppercase">
                  Pitch Deck and Elevator Pitch Video
                </h5>
              </div>
              <div className="pt-3">
                <p className="block mt-1 text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">Pitch deck: </span>{" "}
                  <a
                    href={`${IMAGE_URL}${selectedQuery?.pitchDeck}`}
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-blue-500"
                  >
                    Download pitch deck
                  </a>
                </p>
                <p className="block mt-1 text-base font-ibm-semibold text-black">
                  <span className="font-ibm-regular">
                    Elevator pitch video:{" "}
                  </span>{" "}
                  <a
                    href={`${IMAGE_URL}${selectedQuery?.elevatorPitchVideo}`}
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-blue-500"
                  >
                    Download elevator pitch video
                  </a>
                </p>
              </div>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    </section>
  );
};

export default StartupApplications;
